import React, { useState, useContext, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import { useUser } from '../../services/contexts/user-context';
import { ToastContext } from '../../services/contexts/toast-context';
import ReusableSidebar from '../../components/sidebar/reusable-sidebar';
import { MainContent } from '../../components/sidebar/sidebar.styles';
import {
  ProjectsContainer,
  Header,
  ProjectsGrid,
  ProjectCard,
  ProjectCover,
  ProjectContent,
  ProjectActions,
  PublishedBadge,
  EmptyState,
} from './projects.styles';
import { useNavigate } from 'react-router-dom';
import {
  useGetUserProjects,
  useDeleteProject,
  useToggleProjectPublish,
  useUploadProjectScreenshot,
} from '../../services/api/projects-api';
import ProjectFormDialog from './components/project-form-dialog';
import { Chip } from 'primereact/chip';
import ScreenshotUploader from './components/screenshot-uploader';

export default function ProjectsPage() {
  const toastRef = useContext(ToastContext);
  const { user, isLoading: userLoading } = useUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window === 'undefined' || !window.matchMedia('(max-width: 768px)').matches
  );
  
  // State
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(null);
  const actionMenusRef = useRef({});

  // API Hooks
  const { data: projectsData, isLoading: projectsLoading } = useGetUserProjects();
  const projects = projectsData?.results ?? projectsData ?? [];
  const { mutate: deleteProject, isPending: isDeleting } = useDeleteProject();
  const { mutate: togglePublish, isPending: isToggling } = useToggleProjectPublish();
  const { mutate: uploadScreenshot, isPending: isUploadingScreenshot } = useUploadProjectScreenshot();
  const [screenshotModalVisible, setScreenshotModalVisible] = useState(false);
  const [screenshotProject, setScreenshotProject] = useState(null);

  const handleToggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleCreateNew = () => {
    setEditingProject(null);
    setShowFormDialog(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowFormDialog(true);
  };

  const navigate = useNavigate();

  const openProject = (project) => {
    navigate(`/default/projects/${project.id}`);
  };

  const handleDelete = (projectId) => {
    deleteProject(projectId, {
      onSuccess: () => {
        toastRef.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Project deleted successfully',
          life: 3000,
        });
        setDeleteConfirmDialog(null);
      },
      onError: (error) => {
        toastRef.current.show({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to delete project',
          life: 3000,
        });
      },
    });
  };

  const handlePublishToggle = (project) => {
    togglePublish(
      { projectId: project.id, isPublished: !project.is_published },
      {
        onSuccess: () => {
          toastRef.current.show({
            severity: 'success',
            summary: 'Success',
            detail: !project.is_published ? 'Project published' : 'Project unpublished',
            life: 3000,
          });
        },
        onError: (error) => {
          toastRef.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to update project',
            life: 3000,
          });
        },
      }
    );
  };

  if (projectsLoading || userLoading) {
    return (
      <>
        <ReusableSidebar
          isSidebarOpen={isSidebarOpen}
          handleToggleSidebar={handleToggleSidebar}
          selectedPage="projects"
          user={user}
          loading={true}
        />
        <MainContent isSidebarOpen={isSidebarOpen}>
          <ProjectsContainer>Loading...</ProjectsContainer>
        </MainContent>
      </>
    );
  }

  return (
    <>
      <ReusableSidebar
        isSidebarOpen={isSidebarOpen}
        handleToggleSidebar={handleToggleSidebar}
        selectedPage="projects"
        user={user}
        loading={userLoading}
      />

      <MainContent isSidebarOpen={isSidebarOpen}>
        <ProjectsContainer>
          <Header>
            <h1>My Projects</h1>
            <Button
              label="Create Project"
              icon="pi pi-plus"
              onClick={handleCreateNew}
              className="p-button-primary"
            />
          </Header>

          {projects.length === 0 ? (
            <EmptyState>
              <i className="pi pi-briefcase" />
              <h3>No Projects Yet</h3>
              <p>Start showcasing your work by creating your first project</p>
              <Button
                label="Create Your First Project"
                icon="pi pi-plus"
                onClick={handleCreateNew}
                className="p-button-primary"
              />
            </EmptyState>
          ) : (
            <ProjectsGrid>
              {projects.map((project) => {
                const menuItems = [
                  {
                    label: project.is_published ? 'Unpublish' : 'Publish',
                    icon: project.is_published ? 'pi pi-eye-slash' : 'pi pi-eye',
                    command: () => handlePublishToggle(project),
                  },
                  {
                    label: 'Add Screenshot',
                    icon: 'pi pi-image',
                    command: () => {
                      setScreenshotProject(project);
                      setScreenshotModalVisible(true);
                    },
                  },
                  {
                    label: 'Edit',
                    icon: 'pi pi-pencil',
                    command: () => handleEdit(project),
                  },
                  {
                    label: 'Delete',
                    icon: 'pi pi-trash',
                    className: 'p-menuitem-danger',
                    command: () => setDeleteConfirmDialog(project),
                  },
                ];

                // Determine cover image: use explicit cover, or first screenshot, or placeholder
                const coverImage = project.cover_image_url || 
                  (project.screenshots_urls && project.screenshots_urls.length > 0 
                    ? project.screenshots_urls[0] 
                    : null);

                return (
                  <ProjectCard key={project.id} onClick={() => openProject(project)}>
                    {/* popup menu instance (hidden) and top-left trigger button */}
                    <Menu
                      model={menuItems}
                      ref={(el) => {
                        actionMenusRef.current[project.id] = el;
                      }}
                      popup
                    />
                    <Button
                      icon="pi pi-ellipsis-v"
                      className="p-button-rounded p-button-text p-button-lg action-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        actionMenusRef.current[project.id]?.toggle(e);
                      }}
                      aria-label="Project actions"
                    />

                    <PublishedBadge published={project.is_published}>
                      {project.is_published ? 'Published' : 'Draft'}
                    </PublishedBadge>
                    <ProjectCover image={coverImage}>
                      {!coverImage && <i className="pi pi-image" />}
                    </ProjectCover>

                    <ProjectContent>
                      <h3>{project.title}</h3>
                      <p style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {project.description || 'No description provided'}
                      </p>

                      {project.skills && project.skills.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          {project.skills.slice(0, 3).map((skill) => (
                            <Chip
                              key={skill.id}
                              label={skill.name}
                              className="p-chip-sm"
                              style={{ marginRight: '0.5rem', marginBottom: '0.5rem' }}
                            />
                          ))}
                          {project.skills.length > 3 && (
                            <Chip
                              label={`+${project.skills.length - 3} more`}
                              className="p-chip-sm p-chip-secondary"
                            />
                          )}
                        </div>
                      )}

                      <ProjectActions onClick={(e) => e.stopPropagation()}>
                        {/* actions area intentionally left empty; primary actions live in top-left 3-dot menu */}
                      </ProjectActions>
                    </ProjectContent>
                  </ProjectCard>
                );
              })}
            </ProjectsGrid>
          )}

          <ScreenshotUploader
            visible={screenshotModalVisible}
            onHide={() => { setScreenshotModalVisible(false); setScreenshotProject(null); }}
            project={screenshotProject}
            uploadScreenshot={uploadScreenshot}
            isUploading={isUploadingScreenshot}
            toastRef={toastRef}
          />

          {/* Project Form Dialog */}
          <ProjectFormDialog
            visible={showFormDialog}
            onHide={() => {
              setShowFormDialog(false);
              setEditingProject(null);
            }}
            project={editingProject}
          />

          {/* Delete Confirmation Dialog */}
          <Dialog
            visible={!!deleteConfirmDialog}
            onHide={() => setDeleteConfirmDialog(null)}
            header="Confirm Delete"
            modal
            style={{ width: '400px' }}
          >
            <p>
              Are you sure you want to delete "{deleteConfirmDialog?.title}"? This action cannot
              be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
              <Button
                label="Cancel"
                className="p-button-text p-button-secondary"
                onClick={() => setDeleteConfirmDialog(null)}
              />
              <Button
                label="Delete"
                className="p-button-danger"
                loading={isDeleting}
                onClick={() => handleDelete(deleteConfirmDialog.id)}
              />
            </div>
          </Dialog>
        </ProjectsContainer>
      </MainContent>
    </>
  );
}
